"use client";

import { useTranslation } from "react-i18next";
import {
    DepositLimitPeriodInterval,
    SignupInputFieldName,
    TranslationScopes,
    AssetsDirectory,
} from "@finbackoffice/enums";
import {
    depositLimitsValidationSchema,
    getFormattedDepositLimitPeriod,
} from "@finbackoffice/site-core";
import classNames from "classnames";
import {
    FC,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/base/button/Button";
import Loading from "components/base/loading/Loading";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import {
    IUpdateDepositLimitsV2,
    useNotification,
    useResponsibleGamingV2,
    useSignupConfigV2,
} from "hooks";
import SiteNotification from "components/base/notification/SiteNotification";
import Input from "components/base/input-field/Input";
import UniversalDeviceDropdown from "components/base/universal-device-dropdown/UniversalDeviceDropdown";
import { UserAccountContextV2, IModalForwardRefPropsV2 } from "contexts";
import { RequestError } from "@finbackoffice/fe-core";
import AccountDetailsFormBlock from "../form-block/AccountDetailsFormBlock";
import ConfirmationModal from "./ConfirmationModal";
import styles from "./responsible-gaming.module.sass";

export type IDepositLimitsForm = InferType<typeof depositLimitsValidationSchema>;

const DepositLimits: FC = () => {
    const { signupConfigObj } = useSignupConfigV2([SignupInputFieldName.Password]);
    const { paymentsPeriodConfig, loadingPeriodConfig, updateDepositLimits } =
        useResponsibleGamingV2();
    const { t: commonT } = useTranslation();
    const { t } = useTranslation(TranslationScopes.UserAccount);

    const { setNotification, notification } = useNotification();

    const { userCurrency } = useContext(UserAccountContextV2);

    const [payload, setPayload] = useState<IUpdateDepositLimitsV2 | null>(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const modalRef: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, isValid, errors, isDirty, dirtyFields },
    } = useForm({
        mode: "onChange",
        context: { configObj: { ...signupConfigObj, t: commonT } },
        resolver: yupResolver(depositLimitsValidationSchema),
        defaultValues: { period: "", amount: undefined, password: "" },
    });

    const hasDepositLimit =
        !!paymentsPeriodConfig?.max_period_amounts[0]?.amount &&
        !!paymentsPeriodConfig.max_period_amounts[0]?.period;

    useEffect(() => {
        if (payload) {
            setNotification({ type: "", error: "", message: "" });
            modalRef.current?.open();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload]);

    const onSubmit = useCallback(
        (data: IDepositLimitsForm) => {
            if (isDirty && isValid && !isSubmitting) {
                let changedFields: IDepositLimitsForm = {} as IDepositLimitsForm;
                Object.keys(dirtyFields).forEach((field) => {
                    changedFields = {
                        ...changedFields,
                        [field]: data[field as keyof IDepositLimitsForm],
                    };
                });

                setPayload(changedFields as IUpdateDepositLimitsV2);
            }
        },
        [dirtyFields, isDirty, isSubmitting, isValid],
    );

    const depositLimitsFormSubmit = useCallback(async () => {
        if (payload) {
            setLoading(true);
            try {
                await updateDepositLimits(payload);
                await modalRef.current?.close();
            } catch (err: any) {
                const error: RequestError = err.response?.data;
                if (error.error) {
                    setNotification({
                        type: "error",
                        error: error.error,
                        message: error.message,
                    });
                }
                setLoading(false);
            }
        }
    }, [updateDepositLimits, payload, setNotification]);

    const periodOptions = useMemo(
        () => [
            {
                value: DepositLimitPeriodInterval.DAY.toString(),
                label: t("account_responsible_gaming_period_daily"),
            },
            {
                value: DepositLimitPeriodInterval.WEEK.toString(),
                label: t("account_responsible_gaming_period_weekly"),
            },
            {
                value: DepositLimitPeriodInterval.MONTH.toString(),
                label: t("account_responsible_gaming_period_monthly"),
            },
        ],
        [t],
    );

    const renderLimitNotification = useMemo(
        () =>
            paymentsPeriodConfig && (
                <div className={styles.form}>
                    <p>
                        <Translate
                            tid="account_responsible_gaming_deposit_limit_notification"
                            namespace={TranslationScopes.UserAccount}
                            replace={{
                                period: getFormattedDepositLimitPeriod(
                                    paymentsPeriodConfig.max_period_amounts[0].period,
                                ),
                                amount: paymentsPeriodConfig.max_period_amounts[0].amount,
                            }}
                        />
                    </p>
                </div>
            ),
        [paymentsPeriodConfig],
    );

    const renderDepositLimitsForm = useMemo(
        () => (
            <form
                data-testid="deposit-limits-form"
                autoComplete="off"
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <p>
                    <Translate
                        tid="account_responsible_gaming_deposit_limit_text"
                        namespace={TranslationScopes.UserAccount}
                    />
                </p>
                <Controller
                    control={control}
                    name="period"
                    render={({ field: { onChange, value, name } }) => (
                        <UniversalDeviceDropdown
                            testId="deposit-limit-period"
                            name={name}
                            options={periodOptions}
                            value={value || ""}
                            onChange={onChange}
                            label={t("account_responsible_gaming_period")}
                            placeholder={t("account_responsible_gaming_period_placeholder")}
                            wrapperClassname={styles.field}
                        />
                    )}
                />
                <div className={styles.amount}>
                    <Controller
                        render={({ field: { onChange, value, name } }) => {
                            return (
                                <Input
                                    type="number"
                                    name={name}
                                    onChange={onChange}
                                    value={value}
                                    label={t("account_responsible_gaming_amount")}
                                    error={errors.amount}
                                    data-testid="deposit-limit-amount"
                                />
                            );
                        }}
                        control={control}
                        name="amount"
                    />
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${userCurrency?.toLowerCase()}.svg`}
                        className="svg-icon crypto-currency"
                    />
                </div>
                <Controller
                    render={({ field: { onChange, value, name } }) => {
                        return (
                            <Input
                                wrapperClassname={styles.password}
                                onChange={onChange}
                                value={value}
                                name={name}
                                label={commonT("userData_password")}
                                innerIconClass={classNames("viewPass", showPassword && "showPass")}
                                type={showPassword ? "text" : "password"}
                                onInnerIconClick={() => setShowPassword(!showPassword)}
                                data-testid="deposit-limit-password"
                            />
                        );
                    }}
                    control={control}
                    name="password"
                />
                <section className={styles.btnContainer}>
                    <Button
                        type="submit"
                        variant="primary"
                        data-testid="confirm-deposit-limits-button"
                        disabled={!isDirty || !isValid || isSubmitting}>
                        <Translate
                            tid="account_responsible_gaming_confirm"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </Button>
                </section>
            </form>
        ),
        [
            commonT,
            control,
            errors.amount,
            handleSubmit,
            isDirty,
            isSubmitting,
            isValid,
            onSubmit,
            periodOptions,
            showPassword,
            t,
            userCurrency,
        ],
    );

    const renderLabel = useCallback(
        () => (
            <>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-deposit-limits.svg`}
                    className="svg-icon"
                />

                <Translate
                    tid="account_responsible_gaming_deposit_limits"
                    namespace={TranslationScopes.UserAccount}
                />
            </>
        ),
        [],
    );

    return (
        <AccountDetailsFormBlock renderHeader={renderLabel} testId="deposit-limits-block">
            <div className={styles.compact}>
                <div>
                    {loadingPeriodConfig ? (
                        <Loading />
                    ) : hasDepositLimit ? (
                        renderLimitNotification
                    ) : (
                        renderDepositLimitsForm
                    )}
                </div>
            </div>
            {payload && (
                <ConfirmationModal
                    ref={modalRef}
                    loading={loading}
                    onConfirm={depositLimitsFormSubmit}
                    text={
                        <Translate
                            tid="account_responsible_gaming_deposit_limit_modal_text"
                            namespace={TranslationScopes.UserAccount}
                            replace={{
                                period: t(
                                    `account_responsible_gaming_period_${getFormattedDepositLimitPeriod(
                                        Number(payload.period),
                                    )}`,
                                ),
                            }}
                        />
                    }
                    error={
                        notification.type && (
                            <SiteNotification
                                type={notification.type}
                                title={notification.error}
                                message={notification.message}
                            />
                        )
                    }
                    handleClose={() => {
                        void modalRef.current?.close();
                    }}
                />
            )}
        </AccountDetailsFormBlock>
    );
};

export default DepositLimits;
